class ProductBuyBox extends HTMLElement {
	connectedCallback() {
		if (!this.isConnected) return;

		this.tabs = Array.from(this.querySelectorAll('[data-buy-box-tab]'));
		this.optionInputs = Array.from(this.querySelectorAll('.product-buy-box__option-input'));
		this.sellingPlanInput = this.querySelector('[name="selling_plan"]');
		this.hiddenQuantityInput = this.querySelector('[data-buy-box-quantity-input]');
		this.productFormId = this.hiddenQuantityInput?.getAttribute('form') || this.sellingPlanInput?.getAttribute('form') || '';
		this.quantityInputs = this.productFormId
			? Array.from(document.querySelectorAll(`[form="${this.productFormId}"][name="quantity"]:not([data-buy-box-quantity-input])`))
			: [];

		this.handleTabChange = this.handleTabChange.bind(this);
		this.handleOptionChange = this.handleOptionChange.bind(this);

		if (this.tabs.length > 1) {
			this.tabs.forEach((tab) => {
				const input = tab.querySelector('.product-buy-box__tab-input');

				if (!input) return;

				input.addEventListener('change', this.handleTabChange);
			});
		}

		this.optionInputs.forEach((input) => {
			input.addEventListener('change', this.handleOptionChange);
		});

		const preselectedTab =
			this.tabs.find((tab) => {
				const input = tab.querySelector('.product-buy-box__tab-input');
				return input && input.checked;
			}) || this.tabs[0];

		if (preselectedTab) {
			this.setActiveTab(preselectedTab);
		}

		const selectedOptionInput = this.getSelectedOptionInput();

		if (selectedOptionInput) {
			const optionIndex = selectedOptionInput.dataset.buyBoxIndex;

			this.syncSelectedOptions(optionIndex);
			this.updateTotals(optionIndex);
			this.updateSubscriptionInfo(optionIndex);
			this.updateQuantity(optionIndex);
		}
	}

	disconnectedCallback() {
		if (this.tabs && this.handleTabChange) {
			this.tabs.forEach((tab) => {
				const input = tab.querySelector('.product-buy-box__tab-input');

				if (!input) return;

				input.removeEventListener('change', this.handleTabChange);
			});
		}

		if (!this.optionInputs || !this.handleOptionChange) return;

		this.optionInputs.forEach((input) => {
			input.removeEventListener('change', this.handleOptionChange);
		});
	}

	handleTabChange(event) {
		const activeTab = event.currentTarget.closest('[data-buy-box-tab]');

		if (!activeTab) return;

		this.setActiveTab(activeTab);

		const selectedOptionInput = this.getSelectedOptionInput();

		if (!selectedOptionInput) return;

		const optionIndex = selectedOptionInput.dataset.buyBoxIndex;

		this.updateTotals(optionIndex);
		this.updateSubscriptionInfo(optionIndex);
		this.updateQuantity(optionIndex);
	}

	handleOptionChange(event) {
		const activeInput = event.currentTarget;
		const optionIndex = activeInput.dataset.buyBoxIndex;

		if (!optionIndex) return;

		this.syncSelectedOptions(optionIndex);
		this.updateTotals(optionIndex);
		this.updateSubscriptionInfo(optionIndex);
		this.updateQuantity(optionIndex);
	}

	getSelectedOptionInput() {
		const activeTab = this.tabs.find((tab) => {
			const input = tab.querySelector('.product-buy-box__tab-input');
			return input && input.checked;
		});

		if (activeTab) {
			const activeTabSelectedInput = activeTab.querySelector('.product-buy-box__option-input:checked');

			if (activeTabSelectedInput) return activeTabSelectedInput;
		}

		return this.optionInputs.find((input) => input.checked) || null;
	}

	setActiveTab(activeTab) {
		this.tabs.forEach((tab) => {
			const isActive = tab === activeTab;
			const details = tab.querySelector('[data-buy-box-details]');
			const radio = tab.querySelector('.product-buy-box__radio');
			const input = tab.querySelector('.product-buy-box__tab-input');

			if (input) {
				input.checked = isActive;
			}

			if (details) {
				details.classList.toggle('hidden', !isActive);
			}

			if (radio) {
				radio.classList.toggle('product-buy-box__radio--active', isActive);
			}
		});

		if (!this.sellingPlanInput) return;

		const activeTabInput = activeTab.querySelector('.product-buy-box__tab-input');

		if (activeTabInput && activeTabInput.value === 'subscription') {
			this.sellingPlanInput.removeAttribute('disabled');
		} else {
			this.sellingPlanInput.setAttribute('disabled', 'disabled');
		}
	}

	syncSelectedOptions(optionIndex) {
		this.optionInputs.forEach((input) => {
			input.checked = input.dataset.buyBoxIndex === optionIndex;
		});
	}

	updateTotals(optionIndex) {
		if (!optionIndex) return;

		this.tabs.forEach((tab) => {
			const matchingInput = tab.querySelector(`.product-buy-box__option-input[data-buy-box-index="${optionIndex}"]`);
			const totalEl = tab.querySelector('[data-buy-box-total]');

			if (!matchingInput || !totalEl) return;

			totalEl.textContent = matchingInput.dataset.total || '';
		});
	}

	updateSubscriptionInfo(optionIndex) {
		if (!optionIndex) return;

		const subscriptionInput = this.querySelector(`input[name="product-buy-box-subscription-option"][data-buy-box-index="${optionIndex}"]`);

		if (!subscriptionInput) return;

		const subscriptionDiscount = this.querySelector('[data-buy-box-subscription-discount]');
		const subscriptionSummary = this.querySelector('[data-buy-box-subscription-discount-summary]');
		const periodicityEls = this.querySelectorAll('[data-buy-box-subscription-periodicity]');
		const periodicityInlineEls = this.querySelectorAll('[data-buy-box-subscription-periodicity-inline]');
		const discountValue = subscriptionInput.dataset.subscriptionDiscount;
		const periodicityValue = subscriptionInput.dataset.subscriptionPeriodicity;
		const subscriptionPlanId = subscriptionInput.dataset.subscriptionPlanId;

		if (subscriptionDiscount) {
			subscriptionDiscount.textContent = discountValue ? `${discountValue}%` : '';
		}

		if (subscriptionSummary) {
			subscriptionSummary.classList.toggle('hidden', !discountValue || discountValue === '0');
		}

		periodicityEls.forEach((element) => {
			element.textContent = periodicityValue ? periodicityValue.toUpperCase() : '';
		});
		periodicityInlineEls.forEach((element) => {
			element.textContent = periodicityValue ? periodicityValue.toLowerCase() : '';
		});

		if (subscriptionPlanId && this.sellingPlanInput) {
			this.sellingPlanInput.value = subscriptionPlanId;
		}
	}

	updateQuantity(optionIndex) {
		if (!optionIndex) return;

		const matchingInput = this.optionInputs.find((input) => input.dataset.buyBoxIndex === optionIndex);

		if (!matchingInput) return;

		const quantityValue = matchingInput.dataset.quantity || '1';

		this.quantityInputs.forEach((input) => {
			input.value = quantityValue;
			input.dispatchEvent(new Event('input', { bubbles: true }));
			input.dispatchEvent(new Event('change', { bubbles: true }));
		});

		if (this.hiddenQuantityInput) {
			this.hiddenQuantityInput.value = quantityValue;
			if (this.quantityInputs.length === 0) {
				this.hiddenQuantityInput.removeAttribute('disabled');
			} else {
				this.hiddenQuantityInput.setAttribute('disabled', 'disabled');
			}
		}
	}
}

if (!customElements.get('product-buy-box')) {
	customElements.define('product-buy-box', ProductBuyBox);
}
